import { useEffect, useMemo, useState } from 'react';

/**
 * Hook to manage stage transitions based on time elapsed since a reference date.
 * Returns whether the stage has been reached based on the duration threshold.
 *
 * @param referenceDate - The starting date/time to measure elapsed time from
 * @param durationInSeconds - The duration threshold in seconds
 * @returns Boolean indicating whether the stage duration has been reached
 */
export function useStageTimer(referenceDate: Date | string | null | undefined, durationInSeconds: number): boolean {
  const remainingTime = useMemo(() => {
    if (!referenceDate) {
      return -1;
    }

    const STAGE_MAX_DURATION = durationInSeconds * 1000; // Convert to milliseconds

    const referenceTime = new Date(referenceDate).getTime();
    const timeElapsed = Date.now() - referenceTime;

    return Math.max(0, STAGE_MAX_DURATION - timeElapsed);
  }, [referenceDate, durationInSeconds]);

  const [hasReachedStage, setHasReachedStage] = useState(!remainingTime);

  useEffect(() => {
    if (remainingTime >= 0) {
      const timer = setTimeout(() => {
        setHasReachedStage(true);
      }, remainingTime);

      return () => clearTimeout(timer);
    }
  }, [remainingTime]);

  return hasReachedStage;
}
